import React, { 
  useMemo, 
  useState, 
  forwardRef, 
  useContext 
} from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import { FieldArrayDndFormik, FieldCustom, FieldSelect } from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import { ThemeContext } from 'styled-components'
import { useUserContext } from 'context/UserContext';
import {
  useParams
} from 'react-router-dom'

const ItemSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  number: Yup.string().required('Campo obrigatório'),
});

// eslint-disable-next-line react/prop-types
const ItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Valor"
          name="number"
          info="Número a ser destacado"
          component={FieldCustom}
        />
        <Field
          title="Descrição"
          name="title"
          info="Descrição do número"
          component={FieldCustom}
        />
      </Form>
    </Formik>
  )
})

const NumbersSchema = Yup.object().shape({
  backgroundColor: Yup.string().nullable(true),
  sectionTitle: Yup.string(),
  data: Yup.array().of(ItemSchema)
    .min(1, 'Mínimo de 1 número')
    .max(4, 'Máximo de 4 números ultrapassado, adicione outro módulo de números se pretente adicionar mais informação.')
    .required('Campo obrigatório'),
});

const NumbersEditor = ({ initialProps, onChange }) => {

  const [{
    user
  }] = useUserContext();

  const {
    subweb
  } = useParams()
  
  const themeContext = useContext(ThemeContext)
  const [editorProps, setEditorProps] = useState(initialProps)

  const subwebColor = useMemo(() => (
    user.subwebs.find(sub => (sub.slug === subweb))?.color ?? themeContext.colors.black
  ), [])

  const initialValues = useMemo(() => {
    const { data, sectionTitle, backgroundColor } = initialProps;
    
    return { 
      data, 
      sectionTitle, 
      backgroundColor,
    };
  }, [initialProps]);

  const handleFormChange = newValues => {
    const newProps = {
      ...initialProps,
      ...newValues,
      ...(newValues.backgroundColor === themeContext.colors.black ? { // if the background is black we must change the font color
        titleColor: `${themeContext.colors.white}`,
        textColor: `${themeContext.colors.white}`,
        lineColor: `${themeContext.colors.white}`,
        sectionTitleColor: `${themeContext.colors.white}`
      } : {
        titleColor: '#000',
        textColor: '#000',
        lineColor: '#000',
        sectionTitleColor: '#000'
      }),
      backgroundColor: newValues.backgroundColor, // if null the module will assume the subweb color
      hasSectionTitle: Boolean(newValues.sectionTitle),
    };
    const isValid = NumbersSchema.isValidSync(newProps)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200,
  [editorProps]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={NumbersSchema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          title="Título"
          name="sectionTitle"
          component={FieldCustom}
          info="Título dos números"
        />
        <Field
          title="Cor de fundo"
          name="backgroundColor"
          component={FieldSelect}
          placeholder="Selecione a cor..."
          options={[
            { value: `${themeContext.colors.black}`, label: 'preto' },
            { value: `${themeContext.colors.white}`, label: 'branco' },
            { value: `${themeContext.colors.grey}`, label: 'cinza' },
            { value: `${subwebColor}`, label: 'cor da subweb' }, // null values are not working, so it goes as string
          ]}
        />
        <Field
          name="data"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'number',
              Header: 'Valor',
            },
            {
              accessor: 'title',
              Header: 'Título',
            }
          ]}
          rowKey="title"
          ItemForm={ItemForm}
          maxItems={4}
        />

      </Form>
    </Formik>
  );
};
NumbersEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    source: PropTypes.string,
  }),
};

NumbersEditor.defaultProps = {
  initialProps: {},
};

export default NumbersEditor;
